import React, { useEffect, useState } from 'react';
import { Modal, Button, Dropdown } from 'react-bootstrap';
import { getAllPaymentTypesByLocalId } from '../../../../../../../../../../../../crud/payments_api/payment_types';
import { changeTransactionPaymentType, splitTransactionPaymentType } from '../../../../../../../../../../../../crud/payments_api/payment_transactions';
import { formatCurrency } from '../../../../../../../../../../../../utils/functions';
import { toast } from 'react-toastify';
import { FaX, FaCreditCard, FaTriangleExclamation, FaPlus, FaTrash, FaLayerGroup, FaArrowRight } from 'react-icons/fa6';
import './ChangeMethod.scss';

interface ChangeMethodProps {
    transaction: any;
    show: boolean;
    onHide: () => void;
    onMethodChanged?: () => void;
}

interface PaymentType {
    payment_type_id: number;
    payment_type_name: string;
    is_custom?: boolean;
    custom_payment_type_id?: number;
}

interface SplitPaymentEntry {
    id: number;
    payment_type_id: number | null;
    payment_type_name: string;
    amount: number;
    tip_amount: number;
    custom_payment_type_id?: number;
}

type Mode = 'simple' | 'split';

const ChangeMethod: React.FC<ChangeMethodProps> = ({ transaction, show, onHide, onMethodChanged }) => {
    const [allPaymentTypes, setAllPaymentTypes] = useState<PaymentType[]>([]);
    const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
    const [selectedPaymentType, setSelectedPaymentType] = useState<number | null>(null);
    const [selectedCustomPaymentTypeId, setSelectedCustomPaymentTypeId] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false);
    const [mode, setMode] = useState<Mode>('simple');
    
    // Split mode state
    const [splitPayments, setSplitPayments] = useState<SplitPaymentEntry[]>([]);
    const [nextId, setNextId] = useState(1);

    // Get transaction amounts
    const transactionAmount = transaction?.transaction_amount || 0;
    const transactionTip = transaction?.tip_amount || 0;
    const transactionNet = transaction?.net_amount || (transactionAmount - transactionTip);

    // Load payment types when modal opens
    useEffect(() => {
        if (show && transaction) {
            setLoadingPaymentTypes(true);
            const userContext = localStorage.getItem('user');
            const localId = userContext ? JSON.parse(userContext).local_id : 0;

            getAllPaymentTypesByLocalId(localId)
                .then((response) => {
                    // Store all types for split mode
                    setAllPaymentTypes(response.data);
                    // Filter out the current payment type for simple mode
                    const availableTypes = response.data.filter(
                        (type: PaymentType) => type.payment_type_id !== transaction.payment_type_id
                    );
                    setPaymentTypes(availableTypes);
                    setSelectedPaymentType(null);
                    setSelectedCustomPaymentTypeId(undefined);
                })
                .catch((error) => {
                    console.error('Error fetching payment types:', error);
                    toast.error('Error al cargar los métodos de pago');
                })
                .finally(() => {
                    setLoadingPaymentTypes(false);
                });
        }
    }, [show, transaction]);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setSelectedPaymentType(null);
            setSelectedCustomPaymentTypeId(undefined);
            setPaymentTypes([]);
            setAllPaymentTypes([]);
            setMode('simple');
            setSplitPayments([]);
            setNextId(1);
        }
    }, [show]);

    // Initialize split payments when switching to split mode
    useEffect(() => {
        if (mode === 'split' && splitPayments.length === 0) {
            // Initialize with two entries
            const initialPayments: SplitPaymentEntry[] = [
                {
                    id: 1,
                    payment_type_id: null,
                    payment_type_name: 'Seleccionar método',
                    amount: Math.floor(transactionNet / 2),
                    tip_amount: Math.floor(transactionTip / 2),
                },
                {
                    id: 2,
                    payment_type_id: null,
                    payment_type_name: 'Seleccionar método',
                    amount: Math.ceil(transactionNet / 2),
                    tip_amount: Math.ceil(transactionTip / 2),
                }
            ];
            setSplitPayments(initialPayments);
            setNextId(3);
        }
    }, [mode, splitPayments.length, transactionNet, transactionTip]);

    const handleConfirmSimple = async () => {
        if (!selectedPaymentType || !transaction) return;

        setIsLoading(true);
        try {
            await changeTransactionPaymentType(
                transaction.transaction_id, 
                selectedPaymentType,
                selectedCustomPaymentTypeId
            );
            toast.success('Método de pago cambiado exitosamente');
            onHide();
            if (onMethodChanged) onMethodChanged();
        } catch (error: any) {
            console.error('Error changing payment method:', error);
            const errorMessage = error.response?.data?.message || 'Error al cambiar el método de pago';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmSplit = async () => {
        if (!isValidSplit() || !transaction) return;

        setIsLoading(true);
        try {
            const multiplePaymentMethods = splitPayments.map(payment => ({
                payment_type_id: payment.payment_type_id!,
                amount: payment.amount + payment.tip_amount, // Backend expects total amount
                tip_amount: payment.tip_amount,
                custom_payment_type_id: payment.custom_payment_type_id
            }));

            await splitTransactionPaymentType(transaction.transaction_id, multiplePaymentMethods);
            toast.success(`Transacción dividida en ${splitPayments.length} transacciones`);
            onHide();
            if (onMethodChanged) onMethodChanged();
        } catch (error: any) {
            console.error('Error splitting transaction:', error);
            const errorMessage = error.response?.data?.message || 'Error al dividir la transacción';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirm = () => {
        if (mode === 'simple') {
            handleConfirmSimple();
        } else {
            handleConfirmSplit();
        }
    };

    // Split mode functions
    const addSplitEntry = () => {
        const remainingNet = transactionNet - getSplitTotalNet();
        const remainingTip = transactionTip - getSplitTotalTip();
        
        const newEntry: SplitPaymentEntry = {
            id: nextId,
            payment_type_id: null,
            payment_type_name: 'Seleccionar método',
            amount: Math.max(0, remainingNet),
            tip_amount: Math.max(0, remainingTip),
        };
        setSplitPayments([...splitPayments, newEntry]);
        setNextId(nextId + 1);
    };

    const removeSplitEntry = (id: number) => {
        if (splitPayments.length > 2) {
            setSplitPayments(splitPayments.filter(p => p.id !== id));
        }
    };

    const updateSplitMethod = (id: number, method: PaymentType) => {
        setSplitPayments(splitPayments.map(p =>
            p.id === id
                ? {
                    ...p,
                    payment_type_id: method.payment_type_id,
                    payment_type_name: method.payment_type_name,
                    custom_payment_type_id: method.is_custom ? method.custom_payment_type_id : undefined
                }
                : p
        ));
    };

    const updateSplitAmount = (id: number, amount: number) => {
        setSplitPayments(splitPayments.map(p =>
            p.id === id ? { ...p, amount: Math.max(0, amount) } : p
        ));
    };

    const updateSplitTip = (id: number, tip: number) => {
        setSplitPayments(splitPayments.map(p =>
            p.id === id ? { ...p, tip_amount: Math.max(0, tip) } : p
        ));
    };

    const getSplitTotalNet = () => splitPayments.reduce((sum, p) => sum + p.amount, 0);
    const getSplitTotalTip = () => splitPayments.reduce((sum, p) => sum + p.tip_amount, 0);
    const getSplitTotal = () => getSplitTotalNet() + getSplitTotalTip();

    const isValidSplit = () => {
        const allMethodsSelected = splitPayments.every(p => p.payment_type_id !== null);
        const allAmountsValid = splitPayments.every(p => p.amount >= 0);
        const totalMatches = Math.abs(getSplitTotal() - transactionAmount) < 0.01;
        return allMethodsSelected && allAmountsValid && totalMatches && splitPayments.length >= 2;
    };

    const getBannerInfo = () => {
        if (!transaction) return null;

        // For split mode
        if (mode === 'split') {
            const hasEfectivo = splitPayments.some(p => p.payment_type_id === 1);
            if (hasEfectivo) {
                return {
                    type: 'warning',
                    icon: <FaTriangleExclamation className="banner-icon-fa" />,
                    title: 'Aprobación manual requerida',
                    message: 'Las transacciones en efectivo requerirán aprobación manual en Finanzas → Cajas.'
                };
            }
            return null;
        }

        // For simple mode
        if (!selectedPaymentType) return null;

        const currentIsEfectivo = transaction.payment_type_id === 1; // Assuming 1 is cash
        const newIsEfectivo = selectedPaymentType === 1;
        const isApproved = transaction.approved;

        if (currentIsEfectivo && isApproved && !newIsEfectivo) {
            return {
                type: 'warning',
                icon: <FaTriangleExclamation className="banner-icon-fa" />,
                title: 'Reducción de efectivo en caja',
                message: `Al cambiar este pago, se reducirá ${formatCurrency(transaction.transaction_amount)} del efectivo en la caja registradora.`
            };
        }

        if (!currentIsEfectivo && isApproved && newIsEfectivo) {
            return {
                type: 'warning',
                icon: <FaTriangleExclamation className="banner-icon-fa" />,
                title: 'Aprobación manual requerida',
                message: 'Alguien deberá ir a Finanzas → Cajas para aprobar este pago en efectivo.'
            };
        }

        return null;
    };

    const selectedPaymentTypeName = paymentTypes.find(pt => pt.payment_type_id === selectedPaymentType)?.payment_type_name || 'Seleccionar método';
    const bannerInfo = getBannerInfo();

    const isConfirmDisabled = mode === 'simple' 
        ? !selectedPaymentType || isLoading || paymentTypes.length === 0
        : !isValidSplit() || isLoading;

    return (
        <Modal show={show} onHide={onHide} centered className={`change-method-modal ${mode === 'split' ? 'split-mode' : ''}`}>
            <Modal.Header>
                <div className="form-modal-title-container">
                    <Modal.Title className="form-modal-title">
                        {mode === 'simple' ? 'Cambiar método de pago' : 'Dividir transacción'}
                    </Modal.Title>
                    <div className="modal-subtitle">
                        Transacción {transaction?.transaction_id} • {formatCurrency(transactionAmount)}
                        {transactionTip > 0 && (
                            <span className="tip-info"> (incluye {formatCurrency(transactionTip)} propina)</span>
                        )}
                    </div>
                </div>
                <Button variant="none" className="modal-close-button" onClick={onHide}>
                    <FaX className="close-icon" />
                </Button>
            </Modal.Header>

            <Modal.Body>
                <div className="change-method-content">
                    {/* Mode Selector */}
                    <div className="mode-selector">
                        <button 
                            className={`mode-btn ${mode === 'simple' ? 'active' : ''}`}
                            onClick={() => setMode('simple')}
                        >
                            <FaArrowRight />
                            Cambiar método
                        </button>
                        <button 
                            className={`mode-btn ${mode === 'split' ? 'active' : ''}`}
                            onClick={() => setMode('split')}
                        >
                            <FaLayerGroup />
                            Dividir en múltiples
                        </button>
                    </div>

                    {/* Current Payment Method */}
                    <div className="current-method-section">
                        <div className="section-title">
                            <FaCreditCard className="section-icon" />
                            Método actual
                        </div>
                        <div className="current-method-display">
                            <span className="method-name">{transaction?.payment_type_name}</span>
                            <span className={`method-status ${transaction?.approved ? 'approved' : 'pending'}`}>
                                {transaction?.approved ? 'Aprobado' : 'Pendiente'}
                            </span>
                        </div>
                        {transactionTip > 0 && (
                            <div className="current-amount-breakdown">
                                <span>Neto: {formatCurrency(transactionNet)}</span>
                                <span>Propina: {formatCurrency(transactionTip)}</span>
                            </div>
                        )}
                    </div>

                    {/* Simple Mode: New Payment Method Selection */}
                    {mode === 'simple' && (
                        <div className="new-method-section">
                            <div className="section-title">
                                <FaCreditCard className="section-icon" />
                                Nuevo método
                            </div>
                            
                            {loadingPaymentTypes ? (
                                <div className="loading-methods">Cargando métodos...</div>
                            ) : (
                                <Dropdown className="payment-type-dropdown">
                                    <Dropdown.Toggle 
                                        variant="none" 
                                        className="payment-type-selector"
                                        disabled={paymentTypes.length === 0}
                                    >
                                        {selectedPaymentType ? selectedPaymentTypeName : 'Seleccionar nuevo método'}
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        {paymentTypes.length === 0 ? (
                                            <Dropdown.Item disabled>
                                                No hay otros métodos disponibles
                                            </Dropdown.Item>
                                        ) : (
                                            paymentTypes.map((paymentType) => (
                                                <Dropdown.Item
                                                    key={paymentType.payment_type_id}
                                                    onClick={() => {
                                                        setSelectedPaymentType(paymentType.payment_type_id);
                                                        setSelectedCustomPaymentTypeId(paymentType.is_custom ? paymentType.custom_payment_type_id : undefined);
                                                    }}
                                                >
                                                    {paymentType.payment_type_name}
                                                </Dropdown.Item>
                                            ))
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                    )}

                    {/* Split Mode: Multiple Payment Methods */}
                    {mode === 'split' && (
                        <div className="split-section">
                            <div className="section-title">
                                <FaLayerGroup className="section-icon" />
                                Dividir en múltiples métodos
                            </div>

                            <div className="split-payments-list">
                                {splitPayments.map((payment, index) => (
                                    <div key={payment.id} className="split-payment-entry">
                                        <div className="split-entry-header">
                                            <span className="entry-number">#{index + 1}</span>
                                            {splitPayments.length > 2 && (
                                                <button
                                                    className="remove-entry-btn"
                                                    onClick={() => removeSplitEntry(payment.id)}
                                                    title="Eliminar"
                                                >
                                                    <FaTrash />
                                                </button>
                                            )}
                                        </div>

                                        <Dropdown className="split-method-dropdown">
                                            <Dropdown.Toggle variant="none" className="split-method-toggle">
                                                {payment.payment_type_name}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                {allPaymentTypes.map(method => (
                                                    <Dropdown.Item
                                                        key={method.payment_type_id}
                                                        onClick={() => updateSplitMethod(payment.id, method)}
                                                    >
                                                        {method.payment_type_name}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>

                                        <div className="split-amounts">
                                            <div className="split-amount-group">
                                                <label>Neto</label>
                                                <div className="amount-input-wrapper">
                                                    <span className="currency">$</span>
                                                    <input
                                                        type="text"
                                                        value={payment.amount > 0 ? payment.amount.toLocaleString('es-CL') : ''}
                                                        onChange={(e) => {
                                                            const cleanedValue = e.target.value.replace(/\D/g, '');
                                                            const numValue = cleanedValue === '' ? 0 : parseInt(cleanedValue, 10);
                                                            updateSplitAmount(payment.id, numValue);
                                                        }}
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                            {transactionTip > 0 && (
                                                <div className="split-amount-group tip">
                                                    <label>Propina</label>
                                                    <div className="amount-input-wrapper">
                                                        <span className="currency">$</span>
                                                        <input
                                                            type="text"
                                                            value={payment.tip_amount > 0 ? payment.tip_amount.toLocaleString('es-CL') : ''}
                                                            onChange={(e) => {
                                                                const cleanedValue = e.target.value.replace(/\D/g, '');
                                                                const numValue = cleanedValue === '' ? 0 : parseInt(cleanedValue, 10);
                                                                updateSplitTip(payment.id, numValue);
                                                            }}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button className="add-split-btn" onClick={addSplitEntry}>
                                <FaPlus /> Agregar método
                            </button>

                            {/* Split Totals */}
                            <div className="split-totals">
                                <div className="split-total-row">
                                    <span>Total neto:</span>
                                    <span className={getSplitTotalNet() === transactionNet ? 'match' : 'mismatch'}>
                                        {formatCurrency(getSplitTotalNet())} / {formatCurrency(transactionNet)}
                                    </span>
                                </div>
                                {transactionTip > 0 && (
                                    <div className="split-total-row">
                                        <span>Total propina:</span>
                                        <span className={getSplitTotalTip() === transactionTip ? 'match' : 'mismatch'}>
                                            {formatCurrency(getSplitTotalTip())} / {formatCurrency(transactionTip)}
                                        </span>
                                    </div>
                                )}
                                <div className="split-total-row total">
                                    <span>Total:</span>
                                    <span className={Math.abs(getSplitTotal() - transactionAmount) < 0.01 ? 'match' : 'mismatch'}>
                                        {formatCurrency(getSplitTotal())} / {formatCurrency(transactionAmount)}
                                    </span>
                                </div>
                            </div>

                            {/* Validation message */}
                            {!isValidSplit() && splitPayments.some(p => p.payment_type_id !== null) && (
                                <div className="validation-message">
                                    {Math.abs(getSplitTotal() - transactionAmount) >= 0.01 && (
                                        <span>La suma de los montos debe ser igual al total de la transacción</span>
                                    )}
                                    {splitPayments.some(p => p.payment_type_id === null) && (
                                        <span>Selecciona un método de pago para cada entrada</span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Information Banner */}
                    {bannerInfo && (
                        <div className={`info-banner ${bannerInfo.type}`}>
                            <div className="banner-icon">
                                {bannerInfo.icon}
                            </div>
                            <div className="banner-content">
                                <div className="banner-title">{bannerInfo.title}</div>
                                <div className="banner-message">{bannerInfo.message}</div>
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <div className="form-modal-footer-custom">
                    <Button 
                        variant="none"
                        className="form-button-cancel"
                        onClick={onHide}
                        disabled={isLoading}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="none"
                        className="form-button-create"
                        onClick={handleConfirm}
                        disabled={isConfirmDisabled}
                    >
                        {isLoading ? (
                            <div className="loading-content">
                                <div className="spinner-border spinner-border-sm" role="status" />
                                {mode === 'simple' ? 'Cambiando...' : 'Dividiendo...'}
                            </div>
                        ) : (
                            mode === 'simple' ? 'Confirmar cambio' : `Dividir en ${splitPayments.length} transacciones`
                        )}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ChangeMethod;